import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { RoleService } from 'Services';
import { OnClickEvent, KeyEvent } from 'Utils';
import useGetParameterTableColumns from 'Hooks/tableColumns/useGetParameterTableColumns';
import useGetParameters from 'Hooks/query-hooks/parameters/useGetParameters';
import Table from 'Components/Table';
import PageHeader from 'Components/PageHeader';
import MultiOptionDropdown from 'Components/MultiOptionDropdown';
import { ROUTES } from 'Components/Breadcrumbs/routes';
import ButtonGroup from 'Components/ButtonGroup/ButtonGroup';
import { shouldShowManageVelocities } from 'Selectors';
import DeleteParamModal from './components/DeleteParamModal';
import styles from './Parameters.css';

const Parameters = () => {
    const history = useHistory();
    const parameterAPI = useGetParameters({});
    const { t } = useTranslation();
    const [paramToDelete, setParamToDelete] = React.useState<undefined | ParameterGETResponse>(undefined);

    const onClose = () => {
        setParamToDelete(undefined);
    };

    const handleDelete = <T,>(id?: number, event?: OnClickEvent<T> | KeyEvent<T>) => {
        if (id) {
            event?.stopPropagation();
            setParamToDelete(parameterAPI.data?.idHashmap[id]);
        }
    };

    const columns = useGetParameterTableColumns({ handleDelete });

    const options: MenuItem[] = [
        {
            visibility: true,
            displayText: t('Expression'),
            iconName: 'touchpoint',
            handleNavigation: () => {
                history.push(ROUTES.PARAMETERS.CREATE_NEW_EXPRESSION_PAGE);
            }
        },
        {
            visibility: shouldShowManageVelocities(),
            displayText: t('Velocity'),
            iconName: 'results',
            handleNavigation: () => {
                history.push(ROUTES.PARAMETERS.CREATE_NEW_VELOCITY_PAGE);
            }
        }
    ];

    return (
        <div className={styles.landingPage}>
            <PageHeader>
                <PageHeader.Left className={styles.titleText}>{t('Parameters')}</PageHeader.Left>
                <PageHeader.Right className={styles.rightButtonContainer}>
                    <ButtonGroup direction='row'>
                        {RoleService.isParameterAdmin && (
                            <MultiOptionDropdown
                                id='parameter-multi-option-button'
                                showArrows
                                baseText={t('Add New')}
                                variant='primary'
                                alignment='right'
                                options={options}
                            />
                        )}
                    </ButtonGroup>
                </PageHeader.Right>
            </PageHeader>

            <div className={styles.tableContainer}>
                <Table
                    tableName='parameters-table'
                    showMultiSort
                    showRichFilter
                    isLoading={parameterAPI.isLoading}
                    pathToIdOnDataObj='message'
                    enablePagination
                    initialState={{
                        pageSize: 20,
                        hiddenColumns: ['id'],
                        sortBy: [{ id: 'message', desc: false }]
                    }}
                    dataTestId='parameters-table'
                    noDataMessage='No Parameter found'
                    columns={columns}
                    data={parameterAPI.data?.original ?? []}
                    showExpandableSearch
                    clickableRow
                />
            </div>

            {paramToDelete && <DeleteParamModal onClose={onClose} paramToDelete={paramToDelete} />}
        </div>
    );
};

export default React.memo(Parameters);
